import type { HistoricalApyChartData } from '@/queries/historicalApy.query'
import type { HistoricalBenchmark } from '@/queries/historicalBenchmarks.query'
import type { ApyBreakdown, ApyIntervalBreakdown } from '@vaultsfyi/common/schemas'

type MergedHistoricalChartData = {
  timestamp: number
  [key: string]: number | ApyBreakdown | ApyIntervalBreakdown
}

interface MergeHistoricalDataParams {
  vaultData: HistoricalApyChartData[]
  ethBenchmarkData?: HistoricalBenchmark
  usdBenchmarkData?: HistoricalBenchmark
}

type BenchmarkKey = 'eth-mainnet' | 'usd-mainnet'

const ETH_BENCHMARK_KEY: BenchmarkKey = 'eth-mainnet'
const USD_BENCHMARK_KEY: BenchmarkKey = 'usd-mainnet'

export function mergeHistoricalData({
  vaultData,
  ethBenchmarkData,
  usdBenchmarkData,
}: MergeHistoricalDataParams): MergedHistoricalChartData[] {
  const dataRecord: Record<number, MergedHistoricalChartData> = {}

  for (const dataPoint of vaultData) {
    const { timestamp, ...vaultKeys } = dataPoint
    if (!dataRecord[timestamp]) {
      dataRecord[timestamp] = { timestamp }
    }
    Object.assign(dataRecord[timestamp], vaultKeys)
  }

  const addAndInterpolateBenchmark = (benchmarkData: HistoricalBenchmark | undefined, key: BenchmarkKey): void => {
    if (!benchmarkData || benchmarkData.length === 0) return

    const sortedBenchmarkData = [...benchmarkData].sort((a, b) => a.timestamp - b.timestamp)

    if (Object.keys(dataRecord).length === 0) {
      for (const benchmarkPoint of sortedBenchmarkData) {
        if (benchmarkPoint.apy !== null && benchmarkPoint.apy !== undefined) {
          if (!dataRecord[benchmarkPoint.timestamp]) {
            dataRecord[benchmarkPoint.timestamp] = { timestamp: benchmarkPoint.timestamp }
          }
          dataRecord[benchmarkPoint.timestamp][key] = benchmarkPoint.apy
        }
      }
      return
    }

    const sortedTimestamps = Object.keys(dataRecord)
      .map(Number)
      .sort((a, b) => a - b)

    for (const timestamp of sortedTimestamps) {
      let benchmarkValue: ApyIntervalBreakdown | undefined
      for (let i = sortedBenchmarkData.length - 1; i >= 0; i--) {
        const apy = sortedBenchmarkData[i].apy
        if (sortedBenchmarkData[i].timestamp <= timestamp && apy !== null && apy !== undefined) {
          benchmarkValue = apy
          break
        }
      }

      if (benchmarkValue) {
        dataRecord[timestamp][key] = benchmarkValue
      }
    }
  }

  addAndInterpolateBenchmark(ethBenchmarkData, ETH_BENCHMARK_KEY)
  addAndInterpolateBenchmark(usdBenchmarkData, USD_BENCHMARK_KEY)

  const filteredData = Object.values(dataRecord).filter((point) => Object.keys(point).length > 1)

  return filteredData.sort((a, b) => a.timestamp - b.timestamp)
}
